import { useCallback } from "react";
import { InferenceTypes } from "@functions/rapid/services/inference/stream/types";
import { useGetPredictions } from "./useGetPredictions";
import {
    useMediaStore,
    usePromptLabels,
    useAddToInferenceQueue,
    useUpdateInferenceQueueItemStatus,
    useStore,
    InferenceStatus,
    InferenceQueueItem,
    AnnotationsForFileAndFrameId,
    useSetBoxPromptCache
} from "@/store";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import type { Prediction } from "@/utils/predictionFiltering";
import { crossClassNMSWithExisting } from "@/utils/nms";
import { MediaItem } from "@/types/media";
import {
    separateLabelsByDiffStatus,
    buildBoxPromptingDataForDiffLabels,
    getPromptFrameIds,
    waitForPromptFrameUploads
} from "./useAutoPredictions/utils";

// ============================================================================
// Types
// ============================================================================

export interface RunInferenceParams {
    /** The media item to run inference on */
    mediaItem: MediaItem;
    /** For videos: metadata about the extracted frame */
    extractedFrameMeta?: { videoId: string; segment: number };
    /** Whether predictions should be marked as draft */
    isDraft?: boolean;
    /** Annotations data - required for building box prompting data */
    annotationsForFileAndFrameId: AnnotationsForFileAndFrameId;
    /** Optional abort controller for cancellation */
    abortController?: AbortController;
    /**
     * Optional custom labels for SAM3 text prompting.
     * If provided, overrides the automatic label separation based on hasDiffs.
     */
    sam3Labels?: string[];
    /**
     * Optional custom labels for OWLv2 box prompting.
     * If provided, overrides the automatic label separation based on hasDiffs.
     */
    owlv2Labels?: string[];
    /**
     * Optional confidence threshold for SAM3 text prompting.
     */
    sam3ConfidenceThreshold?: number;
    /**
     * When true, disables confidence filtering at inference time.
     * Used for "test with more files" in REVIEW step where we want to show
     * ALL predictions and let the UI handle filtering via the confidence slider.
     */
    skipConfidenceFiltering?: boolean;
}

interface InferenceContext {
    mediaItem: MediaItem;
    extractedFrameMeta?: { videoId: string; segment: number };
    isDraft: boolean;
    abortController?: AbortController;
    sam3Labels: string[];
    owlv2Labels: string[];
    sam3ConfidenceThreshold?: number;
    boxPromptingData: any[];
    boxConfidenceThreshold: number;
    isParallelInference: boolean;
    /** File ID for caching predictions */
    fileId: string;
    /** Frame ID for caching predictions */
    frameId: string;
    /** Existing annotations for calculating cache index */
    existingAnnotations: Record<string, any[]>;
    /** When true, disables confidence filtering */
    skipConfidenceFiltering: boolean;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Determines which labels should use SAM3 vs OWLv2 inference.
 * If custom labels are provided, uses those. Otherwise separates by hasDiffs.
 */
function resolveLabels(
    promptLabels: Map<string, any>,
    customSam3Labels?: string[],
    customOwlv2Labels?: string[]
): { sam3Labels: string[]; owlv2Labels: string[] } {
    if (customSam3Labels !== undefined || customOwlv2Labels !== undefined) {
        return { sam3Labels: customSam3Labels ?? [], owlv2Labels: customOwlv2Labels ?? [] };
    }

    const { labelsWithDiffs, labelsWithoutDiffs } = separateLabelsByDiffStatus(promptLabels);
    return { sam3Labels: labelsWithoutDiffs, owlv2Labels: labelsWithDiffs };
}

/**
 * Builds a map of media dimensions for all frames that have annotations.
 */
function buildMediaDimensionsMap(
    annotationsForFileAndFrameId: AnnotationsForFileAndFrameId,
    mediaStore: Record<string, MediaItem>,
    defaultDimensions?: { width: number; height: number }
): Map<string, { width: number; height: number }> {
    const dimensions = new Map<string, { width: number; height: number }>();

    Object.entries(annotationsForFileAndFrameId).forEach(([fileId, frames]) => {
        Object.keys(frames).forEach((frameId) => {
            if (dimensions.has(frameId)) return;

            const mediaItem = mediaStore[fileId];
            const itemDimensions =
                mediaItem?.metadata?.naturalDimensions ||
                mediaItem?.metadata?.serverMediaDimensions ||
                defaultDimensions;

            if (itemDimensions) {
                dimensions.set(frameId, itemDimensions);
            }
        });
    });

    return dimensions;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Centralized hook for running inference on media items.
 *
 * This hook encapsulates the logic for determining which inference type(s) to run
 * based on the current state of prompt labels:
 * - Labels WITHOUT user edits (hasDiffs=false): SAM3 text prompting
 * - Labels WITH user edits (hasDiffs=true): OWLv2 box prompting
 *
 * When both types of labels exist, it runs both inference types in parallel
 * and manages the inference queue to ensure the loading overlay stays visible
 * until all inference completes.
 *
 * @example
 * const { runInference } = useRunInference();
 *
 * // Simple usage - automatically separates labels by hasDiffs
 * await runInference({
 *     mediaItem,
 *     annotationsForFileAndFrameId,
 *     isDraft: true
 * });
 *
 * // With custom labels (e.g., from inference strategy)
 * await runInference({
 *     mediaItem,
 *     annotationsForFileAndFrameId,
 *     sam3Labels: ['backpack', 'person'],
 *     owlv2Labels: ['car']
 * });
 */
export const useRunInference = () => {
    const { getPredictionsForFile } = useGetPredictions();
    const { currentWorkspace } = useCurrentWorkspace();
    const workspaceUrl = currentWorkspace?.url ?? "anonymous";
    const media = useMediaStore();
    const promptLabels = usePromptLabels();
    const addToInferenceQueue = useAddToInferenceQueue();
    const updateInferenceQueueItemStatus = useUpdateInferenceQueueItemStatus();
    const setBoxPromptCache = useSetBoxPromptCache();

    /**
     * Starts the inference queue for tracking loading state.
     */
    const startInferenceQueue = useCallback(
        (mediaId: string, abortController?: AbortController) => {
            const queueItem: InferenceQueueItem = {
                id: mediaId,
                status: InferenceStatus.INFERRING,
                promise: Promise.resolve(),
                abortController,
                progress: 0
            };
            addToInferenceQueue(queueItem);
        },
        [addToInferenceQueue]
    );

    /**
     * Marks the inference queue item as completed.
     */
    const completeInferenceQueue = useCallback(
        (mediaId: string) => {
            updateInferenceQueueItemStatus(mediaId, InferenceStatus.COMPLETED);
        },
        [updateInferenceQueueItemStatus]
    );

    /**
     * Runs SAM3 text prompting inference.
     */
    const runSam3Inference = useCallback(
        (ctx: InferenceContext): Promise<void> => {
            return getPredictionsForFile({
                mediaItem: ctx.mediaItem,
                extractedFrameMeta: ctx.extractedFrameMeta,
                inferenceType: InferenceTypes.SAM3,
                textPrompts: ctx.sam3Labels,
                isDraft: ctx.isDraft,
                appendMode: ctx.isParallelInference,
                useInferenceQueue: false,
                abortController: ctx.abortController,
                confidenceThreshold: ctx.sam3ConfidenceThreshold
            });
        },
        [getPredictionsForFile]
    );

    /**
     * Runs OWLv2 box prompting inference and caches predictions for progressive reveal.
     * Waits for prompt frames to be uploaded first (for video support).
     */
    const runOwlv2Inference = useCallback(
        async (ctx: InferenceContext): Promise<void> => {
            // Wait for all prompt frames to be uploaded before starting OWLv2 inference
            // This prevents "Data is not image" errors for video frames still uploading
            const promptFrameIds = getPromptFrameIds(ctx.boxPromptingData);
            if (promptFrameIds.length > 0) {
                try {
                    await waitForPromptFrameUploads(
                        promptFrameIds,
                        () => useStore.getState().media
                    );
                } catch {
                    // Continue anyway - frames might be images that don't need upload
                }
            }

            // Capture raw predictions per label for caching
            const predictionsByLabel = new Map<string, Prediction[]>();

            await getPredictionsForFile({
                mediaItem: ctx.mediaItem,
                extractedFrameMeta: ctx.extractedFrameMeta,
                inferenceType: InferenceTypes.OWLVIT,
                customPromptingData: ctx.boxPromptingData,
                textPrompts: ctx.owlv2Labels,
                // When skipping confidence filtering, let UI handle it via slider
                confidenceThreshold: ctx.skipConfidenceFiltering
                    ? undefined
                    : ctx.boxConfidenceThreshold,
                isDraft: ctx.isDraft,
                appendMode: ctx.isParallelInference,
                useInferenceQueue: false,
                abortController: ctx.abortController,
                // Capture raw predictions for caching
                onRawPredictions: (predictions) => {
                    // Group predictions by label
                    for (const pred of predictions) {
                        const label = pred.class || pred.label || "";
                        if (!predictionsByLabel.has(label)) {
                            predictionsByLabel.set(label, []);
                        }
                        predictionsByLabel.get(label)!.push(pred);
                    }
                }
            });

            // Cache predictions for each OWLv2 label
            for (const label of ctx.owlv2Labels) {
                const labelPredictions = predictionsByLabel.get(label) || [];
                if (labelPredictions.length === 0) continue;

                // Get existing annotations for this label to calculate next reveal index
                const existingForLabel = (ctx.existingAnnotations[label] || []).filter(
                    (a: any) => a.label === label
                );

                // Calculate how many predictions were shown (above threshold and not duplicates)
                let shownCount = 0;
                for (const pred of labelPredictions) {
                    if (pred.confidence >= ctx.boxConfidenceThreshold) {
                        const { keptPredictions } = crossClassNMSWithExisting(
                            [pred],
                            existingForLabel,
                            0.5
                        );
                        if (keptPredictions.length > 0) {
                            shownCount++;
                        }
                    }
                }

                // If no predictions shown above threshold but we have predictions,
                // fallback would have shown one
                if (shownCount === 0 && labelPredictions.length > 0) {
                    shownCount = 1;
                }

                // Cache the predictions for this label
                setBoxPromptCache(ctx.fileId, ctx.frameId, label, labelPredictions, shownCount);
            }
        },
        [getPredictionsForFile, setBoxPromptCache]
    );

    /**
     * Main inference runner.
     */
    const runInference = useCallback(
        async ({
            mediaItem,
            extractedFrameMeta,
            isDraft = false,
            annotationsForFileAndFrameId,
            abortController,
            sam3Labels: customSam3Labels,
            owlv2Labels: customOwlv2Labels,
            sam3ConfidenceThreshold,
            skipConfidenceFiltering = false
        }: RunInferenceParams): Promise<void> => {
            // Resolve which labels go to which inference type
            const { sam3Labels, owlv2Labels } = resolveLabels(
                promptLabels,
                customSam3Labels,
                customOwlv2Labels
            );

            // Nothing to do if no labels
            if (sam3Labels.length === 0 && owlv2Labels.length === 0) {
                return;
            }

            // Build dimensions map for box prompting
            const defaultDimensions =
                mediaItem.metadata?.naturalDimensions || mediaItem.metadata?.serverMediaDimensions;
            const mediaDimensions = buildMediaDimensionsMap(
                annotationsForFileAndFrameId,
                media,
                defaultDimensions
            );

            // Build box prompting data for OWLv2 labels
            const { promptingData: boxPromptingData, confidenceThreshold: boxConfidenceThreshold } =
                buildBoxPromptingDataForDiffLabels(
                    annotationsForFileAndFrameId,
                    owlv2Labels,
                    mediaDimensions,
                    workspaceUrl
                );

            // Determine if running both inference types in parallel
            const isParallelInference =
                sam3Labels.length > 0 && boxPromptingData.length > 0 && owlv2Labels.length > 0;

            // Determine file and frame IDs for caching
            const fileId = extractedFrameMeta?.videoId || mediaItem.id;
            const frameId = mediaItem.id;

            // Get existing annotations for the current frame for cache index calculation
            const existingAnnotations: Record<string, any[]> = {};
            const frameAnnotations = annotationsForFileAndFrameId[fileId]?.[frameId] || [];
            for (const anno of frameAnnotations) {
                const label = anno.label || "";
                if (!existingAnnotations[label]) {
                    existingAnnotations[label] = [];
                }
                existingAnnotations[label].push(anno);
            }

            // Build inference context
            const ctx: InferenceContext = {
                mediaItem,
                extractedFrameMeta,
                isDraft,
                abortController,
                sam3Labels,
                owlv2Labels,
                sam3ConfidenceThreshold,
                boxPromptingData,
                boxConfidenceThreshold,
                isParallelInference,
                fileId,
                frameId,
                existingAnnotations,
                skipConfidenceFiltering
            };

            // Start tracking in inference queue
            if (mediaItem.id) {
                startInferenceQueue(mediaItem.id, abortController);
            }

            // Build list of inference promises
            const inferencePromises: Promise<void>[] = [];

            if (sam3Labels.length > 0) {
                inferencePromises.push(runSam3Inference(ctx));
            }

            if (boxPromptingData.length > 0 && owlv2Labels.length > 0) {
                inferencePromises.push(runOwlv2Inference(ctx));
            }

            // Run all inference in parallel
            await Promise.all(inferencePromises);

            // Mark as completed
            if (mediaItem.id) {
                completeInferenceQueue(mediaItem.id);
            }
        },
        [
            promptLabels,
            media,
            workspaceUrl,
            startInferenceQueue,
            completeInferenceQueue,
            runSam3Inference,
            runOwlv2Inference
        ]
    );

    return { runInference };
};
